import { useQueryClient } from '@tanstack/react-query';
import {
  AuthError,
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes as cognitoUpdateUserAttributes,
  signIn,
  signInWithRedirect,
  getCurrentUser,
  signOut,
  confirmSignIn,
  resetPassword as cognitoResetPassword,
  confirmResetPassword as cognitoConfirmResetPassword,
  AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { createContext, PropsWithChildren, useContext, useState, useEffect } from 'react';

import { useLogger } from '@/hooks/useLogger';
import { User, UserAttribute, CognitoGroup } from '@/models/User';
import { requestSelfSignup } from '@/services/api/invites';
import { setAccessToken, removeAccessToken } from '@/utils/authTokens';
import { identifyHotjar } from '@/utils/hotjar';

interface AuthContextProps {
  user: User | null;
  initializeAuth: () => Promise<void>;
  logIn: (email: string, password: string) => Promise<'SIGNED_IN' | 'NEW_PASSWORD_REQUIRED'>;
  completeNewPassword: (
    newPassword: string,
    userAttributes?: Record<string, string>,
  ) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, newPassword: string, code: string) => Promise<void>;
  resendRegisterCode: (email: string) => Promise<void>;
  logOut: () => Promise<void>;
  refreshAuthSession: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  updateUserAttributes: (attributes: Partial<Record<UserAttribute, string>>) => Promise<void>;
  isAuthInitialized: boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  isResettingPassword: boolean;
  isConfirmingResetPassword: boolean;
  isResendingRegisterCode: boolean;
  isRefreshingAuthSession: boolean;
  authLoading: boolean;
}

export const AuthContext = createContext<AuthContextProps | null>(null);

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error('useAuthContext context must be used inside AuthProvider');

  return context;
};

const isGoogleUser = (user: AuthUser | null): boolean =>
  user?.username?.includes('google') ?? false;

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isConfirmingResetPassword, setIsConfirmingResetPassword] = useState(false);
  const [isResendingRegisterCode, setIsResendingRegisterCode] = useState(false);
  const [isRefreshingAuthSession, setIsRefreshingAuthSession] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  const queryClient = useQueryClient();
  const logger = useLogger('AuthProvider');

  const fetchUser = async (): Promise<void> => {
    try {
      setAuthLoading(true);
      const currentUser = await getCurrentUser();

      try {
        const { tokens } = await fetchAuthSession();
        if (tokens?.accessToken) {
          setAccessToken(tokens.accessToken.toString());
        }

        const attributes = await fetchUserAttributes();

        // Get Cognito Groups from ID token to determine roles
        const groups = (tokens?.idToken?.payload['cognito:groups'] as string[]) || [];
        const resolvedRoles: CognitoGroup[] = groups.filter((g): g is CognitoGroup =>
          Object.values(CognitoGroup).includes(g as CognitoGroup),
        );
        const roles = resolvedRoles.length > 0 ? resolvedRoles : [CognitoGroup.Buyer];

        setUser({
          id: attributes.sub || currentUser.username,
          name: attributes.name || '',
          firstName: attributes.given_name || '',
          lastName: attributes.family_name || '',
          email: attributes.email || '',
          isProviderUser: isGoogleUser(currentUser),
          isProfileComplete: attributes[UserAttribute.isProfileComplete] === 'true',
          roles,
        });
      } catch (attributeError) {
        logger.error('Error fetching user attributes:', { attributeError });

        if (
          attributeError instanceof Error &&
          attributeError.message.includes('Access Token does not have required scopes')
        ) {
          console.warn('Token scope issue detected - refreshing session');

          await signOut();
          removeAccessToken();
          setUser(null);
          throw new Error('Session token lacks required permissions. Please sign in again.');
        }

        removeAccessToken();
        setUser(null);
      }
    } catch (error) {
      removeAccessToken();
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  };

  const initializeAuth = async () => {
    setIsAuthInitialized(false);
    await fetchUser();
    setIsAuthInitialized(true);
  };

  const logIn = async (
    email: string,
    password: string,
  ): Promise<'SIGNED_IN' | 'NEW_PASSWORD_REQUIRED'> => {
    try {
      setIsLoggingIn(true);
      setAuthLoading(true);
      const result = await signIn({ username: email, password });

      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return 'NEW_PASSWORD_REQUIRED';
      }

      if (!result.isSignedIn) {
        throw new Error(
          `Unsupported sign-in step: ${result.nextStep.signInStep}. Please contact support.`,
        );
      }

      await fetchUser();
      return 'SIGNED_IN';
    } catch (error) {
      if (error instanceof AuthError && error.name === 'UserAlreadyAuthenticatedException') {
        await logOut();
        return logIn(email, password);
      }

      throw error;
    } finally {
      setIsLoggingIn(false);
      setAuthLoading(false);
    }
  };

  const completeNewPassword = async (
    newPassword: string,
    userAttributes?: Record<string, string>,
  ) => {
    try {
      setIsLoggingIn(true);
      setAuthLoading(true);
      const result = await confirmSignIn({
        challengeResponse: newPassword,
        ...(userAttributes && { options: { userAttributes } }),
      });

      if (!result.isSignedIn) {
        throw new Error(
          `Unsupported sign-in step: ${result.nextStep.signInStep}. Please contact support.`,
        );
      }

      await fetchUser();
    } finally {
      setIsLoggingIn(false);
      setAuthLoading(false);
    }
  };

  const logOut = async () => {
    try {
      setIsLoggingOut(true);
      setUser(null);
      removeAccessToken();
      await signOut({ global: false });
      queryClient.invalidateQueries();
      queryClient.clear();
    } catch (error) {
      logger.error('Error during sign out', { error });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const resendRegisterCode = async (email: string) => {
    try {
      setIsResendingRegisterCode(true);
      await requestSelfSignup(email);
    } finally {
      setIsResendingRegisterCode(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsResettingPassword(true);
      await cognitoResetPassword({ username: email });
    } finally {
      setIsResettingPassword(false);
    }
  };
  const confirmResetPassword = async (email: string, newPassword: string, code: string) => {
    try {
      setIsConfirmingResetPassword(true);
      await cognitoConfirmResetPassword({
        username: email,
        newPassword,
        confirmationCode: code,
      });
    } finally {
      setIsConfirmingResetPassword(false);
    }
  };

  const refreshAuthSession = async () => {
    try {
      setIsRefreshingAuthSession(true);

      const { tokens } = await fetchAuthSession({ forceRefresh: true });
      const { accessToken } = tokens ?? {};

      if (accessToken) {
        setAccessToken(accessToken.toString());
      }
    } catch {
      await logOut();
    } finally {
      setIsRefreshingAuthSession(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setIsLoggingIn(true);
      setAuthLoading(true);
      await signInWithRedirect({
        provider: 'Google',
      });
    } catch (error) {
      logger.error('Error signing in with Google', { error });

      if (
        error instanceof Error &&
        error.message.includes('An account with this email already exists')
      ) {
        throw new Error(
          'An account with this email already exists. Please sign in with email and password.',
        );
      }
      throw error;
    } finally {
      setIsLoggingIn(false);
      setAuthLoading(false);
    }
  };

  const signInWithApple = async () => {
    try {
      setIsLoggingIn(true);
      setAuthLoading(true);
      await signInWithRedirect({
        provider: 'Apple',
      });
    } catch (error) {
      logger.error('Error signing in with Apple', { error });

      if (
        error instanceof Error &&
        error.message.includes('An account with this email already exists')
      ) {
        throw new Error(
          'An account with this email already exists. Please sign in with email and password.',
        );
      }
      throw error;
    } finally {
      setIsLoggingIn(false);
      setAuthLoading(false);
    }
  };

  const updateUserAttributes = async (attributes: Partial<Record<UserAttribute, string>>) => {
    await cognitoUpdateUserAttributes({ userAttributes: attributes });
    await fetchUser();
  };

  const onAuthEvent = (event: string) => {
    switch (event) {
      case 'signIn':
        fetchUser().catch(logger.error);
        break;
      case 'signOut':
        setUser(null);
        break;
      case 'signIn_failure':
        break;
      case 'tokenRefresh':
      case 'tokenRefresh_failure':
        break;
      case 'configured':
        fetchUser().catch(logger.error);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const cancelListener = Hub.listen('auth', (data) => onAuthEvent(data.payload.event));

    initializeAuth().catch((error) => {
      logger.error('Error during auth initialization', { error });
    });

    return () => {
      cancelListener();
    };
  }, []);

  useEffect(() => {
    if (user && user.id) {
      identifyHotjar(user.id, {
        email: user.email || '',
        name: user.name || '',
      });
    } else {
      identifyHotjar(null, {});
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        initializeAuth,
        logIn,
        completeNewPassword,
        logOut,
        resetPassword,
        confirmResetPassword,
        resendRegisterCode,
        refreshAuthSession,
        signInWithGoogle,
        signInWithApple,
        updateUserAttributes,
        isAuthInitialized,
        isLoggingIn,
        isLoggingOut,
        isResettingPassword,
        isConfirmingResetPassword,
        isResendingRegisterCode,
        isRefreshingAuthSession,
        authLoading,
      }}>
      {children}
    </AuthContext.Provider>
  );
};
